import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@ascend/shared/components';
import {
  DataTable,
  FilterTabs,
  PaginationControls,
  SearchBar,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../components/TableControls';
import { ADMIN_MARGIN_ACCOUNTS, ADMIN_MARGIN_ACCOUNT_STATS } from '../data/mockAdapter';

// ─── Types ───────────────────────────────────────────────────────────────────

type AccountRow = (typeof ADMIN_MARGIN_ACCOUNTS)[number];
type AccountStatus = AccountRow['status'];
type AccountSortKey = 'id' | 'wallet' | 'status' | 'collateralValue' | 'borrowed' | 'availableCredit' | 'healthFactor' | 'facilityName';
type StatusFilter = 'all' | AccountStatus;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'warn', label: 'Warn' },
  { value: 'default', label: 'Default' },
  { value: 'paused', label: 'Paused' },
  { value: 'frozen', label: 'Frozen' },
  { value: 'liquidated', label: 'Liquidated' },
];

const STATUS_CLASS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  warn: 'bg-yellow-100 text-yellow-700',
  default: 'bg-red-100 text-red-700',
  paused: 'bg-orange-100 text-orange-700',
  frozen: 'bg-blue-100 text-blue-700',
  liquidated: 'bg-red-100 text-red-700',
};


// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return `$${value.toLocaleString()}`;
}

function healthFactorColor(hf: number): string {
  if (hf >= 1.5) return 'text-green-600';
  if (hf >= 1.2) return 'text-yellow-600';
  return 'text-red-600';
}

function fmtLargeValue(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

// ─── StatCard ────────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, icon }: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}

// ─── Page Component ──────────────────────────────────────────────────────────

export function MarginAccountsPage() {
  const navigate = useNavigate();
  const [accountRows, setAccountRows] = useState(ADMIN_MARGIN_ACCOUNTS);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);
  const [sortKey, setSortKey] = useState<AccountSortKey>('healthFactor');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  function toggleSort(key: AccountSortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  const filtered = useMemo(() => {
    let result = [...accountRows];
    if (filter !== 'all') result = result.filter((a) => a.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.wallet.toLowerCase().includes(q) ||
          a.id.toLowerCase().includes(q) ||
          a.facilityName.toLowerCase().includes(q),
      );
    }
    result.sort((a, b) => {
      const cmp =
        typeof a[sortKey] === 'number'
          ? (a[sortKey] as number) - (b[sortKey] as number)
          : String(a[sortKey]).localeCompare(String(b[sortKey]));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [accountRows, filter, search, sortDir, sortKey]);

  const paginated = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    return filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  }, [filtered, page, pageSize]);

  const filterCounts = useMemo(() => {
    const counts: Partial<Record<StatusFilter, number>> = { all: accountRows.length };
    for (const a of accountRows) {
      counts[a.status as StatusFilter] = (counts[a.status as StatusFilter] ?? 0) + 1;
    }
    return counts;
  }, [accountRows]);

  function handleToggleFreeze(account: AccountRow) {
    const isFrozen = account.status === 'frozen';
    setAccountRows((prev) =>
      prev.map((a) => (a.id === account.id ? { ...a, status: isFrozen ? 'active' : 'frozen' } : a)),
    );
    if (isFrozen) {
      toast.success(`Account ${account.id} unfrozen`);
    } else {
      toast.error(`Account ${account.id} frozen`);
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'id',
        label: 'Account ID',
        sortable: true,
        sortKey: 'id' as const,
        renderCell: (a: AccountRow) => <span className="font-mono text-xs">{a.id}</span>,
      },
      {
        id: 'wallet',
        label: 'Wallet',
        sortable: true,
        sortKey: 'wallet' as const,
        renderCell: (a: AccountRow) => <span className="font-mono text-xs">{a.wallet}</span>,
      },
      {
        id: 'facilityName',
        label: 'Linked CF',
        sortable: true,
        sortKey: 'facilityName' as const,
        renderCell: (a: AccountRow) => (
          <Link
            to={`/cf/${encodeURIComponent(a.facilityName)}`}
            className="text-sm text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {a.facilityName}
          </Link>
        ),
      },
      {
        id: 'collateralValue',
        label: 'Collateral',
        align: 'right' as const,
        sortable: true,
        sortKey: 'collateralValue' as const,
        renderCell: (a: AccountRow) => (
          <div>
            <div>{formatCurrency(a.collateralValue)}</div>
            {a.collateralTokens && (
              <div className="text-xs text-muted-foreground truncate max-w-[180px]">{a.collateralTokens}</div>
            )}
          </div>
        ),
      },
      {
        id: 'borrowed',
        label: 'Borrowed',
        align: 'right' as const,
        sortable: true,
        sortKey: 'borrowed' as const,
        renderCell: (a: AccountRow) => formatCurrency(a.borrowed),
      },
      {
        id: 'availableCredit',
        label: 'Available Credit',
        align: 'right' as const,
        sortable: true,
        sortKey: 'availableCredit' as const,
        renderCell: (a: AccountRow) => formatCurrency(a.availableCredit),
      },
      {
        id: 'healthFactor',
        label: 'Health Factor',
        align: 'right' as const,
        sortable: true,
        sortKey: 'healthFactor' as const,
        renderCell: (a: AccountRow) => (
          <span className={healthFactorColor(a.healthFactor)}>{a.healthFactor.toFixed(2)}</span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        sortKey: 'status' as const,
        renderCell: (a: AccountRow) => (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_CLASS[a.status] ?? ''}`}>
            {a.status}
          </span>
        ),
      },
      {
        id: 'actions',
        label: 'Actions',
        align: 'right' as const,
        renderCell: (a: AccountRow) => (
          <details
            className="relative inline-block text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <summary className="cursor-pointer list-none rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-accent select-none inline-flex items-center gap-1">
              Actions <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-3 w-3 opacity-60" />
            </summary>
            <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-border bg-card shadow-md py-1">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (a.status === 'liquidated') {
                    toast.error(`Account ${a.id} is liquidated`);
                    return;
                  }
                  handleToggleFreeze(a);
                }}
                disabled={a.status === 'liquidated'}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-accent disabled:opacity-50"
              >
                {a.status === 'frozen' ? 'Unfreeze account' : 'Freeze account'}
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  toast.success(`Clawback initiated on ${a.id} (mock)`);
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-accent"
              >
                Clawback
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  toast.success(`Migration started for ${a.id} (mock)`);
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-accent"
              >
                Migrate / recover
              </button>
            </div>
          </details>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Margin Accounts</h1>
          <p className="text-sm text-muted-foreground">
            Global view of all margin accounts across credit facilities with operational controls.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total Accounts"
          value={String(ADMIN_MARGIN_ACCOUNT_STATS.totalAccounts)}
          subtitle="Across all facilities"
          icon={<Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Collateral"
          value={fmtLargeValue(ADMIN_MARGIN_ACCOUNT_STATS.totalCollateral)}
          subtitle="All accounts"
          icon={<Icon name="streamline:money-safe-vault-saving-combo-payment-safe-money-combination-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Borrowed"
          value={fmtLargeValue(ADMIN_MARGIN_ACCOUNT_STATS.totalBorrowed)}
          subtitle="Outstanding borrows"
          icon={<Icon name="streamline:money-cash-bag-dollar-bag-payment-cash-money-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Avg Health Factor"
          value={ADMIN_MARGIN_ACCOUNT_STATS.avgHealthFactor.toFixed(2)}
          subtitle="Global average"
          icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-5 w-5 text-muted-foreground" />}
        />
      </div>

      {/* Filters & search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <FilterTabs<StatusFilter>
          filters={STATUS_FILTERS}
          active={filter}
          onChange={(value) => {
            setFilter(value);
            setPage(1);
          }}
          counts={filterCounts}
        />
        <SearchBar
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search by wallet, account ID, or facility"
          className="w-full sm:w-80"
        />
      </div>

      {/* Table */}
      <DataTable<AccountRow, AccountSortKey>
        columns={columns}
        rows={paginated}
        rowKey={(row) => row.id}
        sort={{
          key: sortKey,
          direction: sortDir,
          onSort: toggleSort,
        }}
        onRowClick={(row) => navigate(`/margin-accounts/${row.id}`)}
        rowClassName={() => 'cursor-pointer'}
        emptyMessage="No margin accounts match your filters."
      />

      {/* Pagination */}
      <PaginationControls
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={filtered.length}
        label="margin accounts"
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
      />
    </div>
  );
}
